import React from 'react';
import PropTypes from 'prop-types';
import { withStateHandlers } from 'recompose';
import classnames from 'classnames';

import { Paragraph, Text, H3 } from 'components/atoms/Typography';
import { Image, Button, Link, MarkdownWrapper } from 'components/atoms';
import FeaturesItem from 'components/facility/FacilityAmenitiesAccordion/FeaturesItem';
import AccessHours from 'components/facility/FacilityAmenitiesAccordion/AccessHours';

import ScrollUpArrow from 'assets/images/icons/psp/accordion/ScrollUpArrow.svg';
import ScrollDownArrow from 'assets/images/icons/psp/accordion/ScrollDownArrow.svg';
import AccordionExpand from 'assets/images/features-amenities/accordion-expand.svg';
import AccordionContract from 'assets/images/features-amenities/accordion-contract.svg';

import './accordion.scss';

export const enhance = withStateHandlers(
	{
		copyExpanded: false,
	},
	{
		handleExpandCopy:
			({ copyExpanded }) =>
			() => ({ copyExpanded: !copyExpanded }),
	},
);

export function AccordionItem({
	question,
	answer,
	className,
	handleExpandCopy,
	copyExpanded,
	link,
	contentNode,
	titleIcon,
	type,
	iconType,
}) {
	// Determine which icon set to use based on iconType prop
	const expandIcon =
		iconType === 'expand' ? AccordionExpand : ScrollDownArrow;
	const contractIcon =
		iconType === 'expand' ? AccordionContract : ScrollUpArrow;

	return (
		<div
			className={classnames(className, 'accordion-item', {
				expanded: copyExpanded,
			})}
		>
			<Button
				onClick={handleExpandCopy}
				className="expand-button"
				aria-expanded={copyExpanded}
			>
				<Text
					weight="bold"
					className="item-title"
				>
					{titleIcon && (
						<Image
							src={titleIcon}
							className="title-icon"
							alt={`${question} icon`}
						/>
					)}
					<H3
						size="medium"
						className="item-title-label"
					>
						{question}
					</H3>
				</Text>

				<Image
					src={copyExpanded ? contractIcon : expandIcon}
					className="expand-button-image"
					alt={copyExpanded ? 'Collapse section' : 'Expand section'}
				/>
			</Button>

			<div
				className={classnames(
					'expand-content',
					copyExpanded
						? 'expand-content--visible'
						: 'expand-content--hidden',
				)}
				aria-hidden={!copyExpanded}
			>
				{contentNode || (
					<Paragraph
						weight="light"
						last
						className="item-answer"
					>
						{type === 'facility' ? (
							<FeaturesItem featuresData={answer} />
						) : type === 'accessHours' ? (
							<AccessHours
								accessHours={answer.accessHours}
								officeHours={answer.officeHours}
							/>
						) : (
							<MarkdownWrapper
								content={[
									{ type: 'faq_content', value: answer },
								]}
							/>
						)}
					</Paragraph>
				)}

				{link && (
					<H3
						weight="bold"
						last
						className="copy-link"
					>
						<Link href={link}>More info on {question}</Link>
					</H3>
				)}
			</div>
		</div>
	);
}

AccordionItem.propTypes = {
	question: PropTypes.string,
	answer: PropTypes.string,
	className: PropTypes.string,
	handleExpandCopy: PropTypes.func,
	copyExpanded: PropTypes.bool,
	link: PropTypes.string,
	contentNode: PropTypes.node,
	titleIcon: PropTypes.string,
	type: PropTypes.string,
	iconType: PropTypes.string,
};

AccordionItem.defaultProps = {
	question: '',
	answer: '',
	className: '',
	handleExpandCopy: () => {},
	copyExpanded: false,
	link: '',
	contentNode: null,
	titleIcon: '',
};

export default enhance(AccordionItem);
